<template>
  <section class="gt-page">
    <div class="gt-page-inner">
      <div class="hero gt-card">
        <div>
          <span class="gt-pill">Administracion de cuenta</span>
          <h1>Mi perfil y suscripcion</h1>
          <p class="gt-muted">Consulta datos administrativos, usuarios, sucursales, modulos activos, limites y pagos.</p>
        </div>
        <button class="gt-button primary" @click="loadSummary" :disabled="loading">Actualizar</button>
      </div>

      <div v-if="message" class="notice" :class="{ error: messageType === 'error' }">{{ message }}</div>

      <div class="tabs gt-card">
        <button v-for="tab in tabs" :key="tab.key" :class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">
          {{ tab.label }}
        </button>
      </div>

      <section v-if="activeTab === 'perfil'" class="gt-grid cols-2 account-grid">
        <article class="gt-card panel">
          <h2>Mi perfil</h2>
          <label class="gt-label">Nombre</label>
          <input v-model="profileForm.name" class="gt-input" placeholder="Tu nombre" />
          <label class="gt-label">Nombre(s)</label>
          <input v-model="profileForm.first_name" class="gt-input" placeholder="Opcional" />
          <label class="gt-label">Apellidos</label>
          <input v-model="profileForm.last_name" class="gt-input" placeholder="Opcional" />
          <p class="gt-muted email-line">{{ user?.email }}</p>
          <button class="gt-button primary" @click="saveProfile" :disabled="saving">Guardar perfil</button>
        </article>

        <article class="gt-card panel">
          <h2>Datos de empresa</h2>
          <label class="gt-label">Nombre comercial</label>
          <input v-model="companyForm.commercial_name" class="gt-input" />
          <label class="gt-label">Razon social</label>
          <input v-model="companyForm.legal_name" class="gt-input" />
          <label class="gt-label">RFC</label>
          <input v-model="companyForm.rfc" class="gt-input" />
          <div class="gt-grid cols-2 compact-grid">
            <div>
              <label class="gt-label">Correo</label>
              <input v-model="companyForm.email" class="gt-input" />
            </div>
            <div>
              <label class="gt-label">Telefono</label>
              <input v-model="companyForm.phone" class="gt-input" />
            </div>
          </div>
          <button class="gt-button primary" @click="saveCompany" :disabled="saving">Guardar empresa</button>
        </article>
      </section>

      <section v-if="activeTab === 'suscripcion'" class="account-grid">
        <div class="gt-grid cols-3">
          <article class="gt-card stat">
            <span>Plan</span>
            <strong>{{ subscription?.plan_name || 'Sin plan' }}</strong>
            <small>{{ subscription?.status || 'pendiente' }}</small>
          </article>
          <article class="gt-card stat">
            <span>Renovacion / prueba</span>
            <strong>{{ daysRemainingText }}</strong>
            <small>{{ formatDate(subscription?.renewal_at) }}</small>
          </article>
          <article class="gt-card stat">
            <span>Periodo</span>
            <strong>{{ subscription?.renewal_period || 'mensual' }}</strong>
            <small>{{ subscription?.currency || 'MXN' }}</small>
          </article>
        </div>

        <div class="gt-card panel">
          <div class="section-header">
            <div>
              <h2>Uso del plan</h2>
              <p class="gt-muted">Cuando un limite se alcanza, el flujo envia a pasarela para ampliar el plan.</p>
            </div>
            <button class="gt-button accent" @click="goToSystemCheckout">Mejorar plan</button>
          </div>
          <div class="gt-grid cols-3">
            <article v-for="item in usageItems" :key="item.key" class="usage-box">
              <span>{{ item.label }}</span>
              <strong>{{ item.used }} / {{ item.limit }}</strong>
              <div class="progress"><i :style="{ width: item.percent + '%' }"></i></div>
            </article>
          </div>
        </div>
      </section>

      <section v-if="activeTab === 'sucursales'" class="account-grid">
        <article class="gt-card panel">
          <div class="section-header">
            <div>
              <h2>Sucursales</h2>
              <p class="gt-muted">Agrega sucursales hasta el limite de tu plan.</p>
            </div>
            <span class="gt-pill">{{ branches.length }} registradas</span>
          </div>

          <form class="inline-form" @submit.prevent="createBranch">
            <input v-model="branchForm.name" class="gt-input" placeholder="Nombre de nueva sucursal" />
            <input v-model="branchForm.city" class="gt-input" placeholder="Ciudad" />
            <button class="gt-button primary" :disabled="saving">Agregar sucursal</button>
          </form>

          <div class="item-list">
            <div v-for="branch in branches" :key="branch.token || branch.id" class="item-row">
              <div>
                <strong>{{ branch.name }}</strong>
                <small>{{ branch.city || 'Sin ciudad' }} · {{ branch.status }}</small>
              </div>
              <span class="gt-pill">{{ branch.token }}</span>
            </div>
          </div>
        </article>
      </section>

      <section v-if="activeTab === 'modulos'" class="account-grid">
        <article class="gt-card panel">
          <div class="section-header">
            <div>
              <h2>Modulos</h2>
              <p class="gt-muted">Los modulos inactivos se activan por pasarela cuando tienen precio Stripe configurado.</p>
            </div>
          </div>
          <div class="module-grid">
            <article v-for="module in modules" :key="module.code" class="module-card">
              <span class="gt-pill" :class="{ active: module.active }">{{ module.active ? 'Activo' : 'Inactivo' }}</span>
              <h3>{{ module.name }}</h3>
              <p>{{ module.description }}</p>
              <small>{{ module.category }} · {{ money(module.monthly_price, module.currency) }}/mes</small>
              <div class="module-actions">
                <button v-if="!module.active" class="gt-button primary" @click="activateModule(module.code)">Activar</button>
                <button v-if="!module.active && canTestActivate" class="gt-button soft" @click="activateForTesting(module.code)">Prueba</button>
                <button v-if="module.code === 'pos' && module.active" class="gt-button accent" @click="$router.push('/pv')">Entrar a PV</button>
              </div>
            </article>
          </div>
        </article>
      </section>

      <section v-if="activeTab === 'usuarios'" class="account-grid">
        <article class="gt-card panel">
          <div class="section-header">
            <div>
              <h2>Usuarios</h2>
              <p class="gt-muted">Para altas nuevas usa invitaciones con rol y sucursal.</p>
            </div>
            <button class="gt-button primary" @click="$router.push('/admin/invitaciones')">Crear invitacion</button>
          </div>
          <div class="item-list">
            <div v-for="item in users" :key="item.token" class="item-row">
              <div>
                <strong>{{ item.name }}</strong>
                <small>{{ item.email }} · {{ item.member_status }}</small>
              </div>
              <span class="gt-pill">{{ item.status || item.user_status }}</span>
            </div>
          </div>
        </article>
      </section>

      <section v-if="activeTab === 'pagos'" class="account-grid">
        <article class="gt-card panel">
          <h2>Pagos y pasarela</h2>
          <p class="gt-muted">Aqui quedo preparado el panel administrativo de facturacion. Cuando Stripe quede configurado, los botones enviaran a Checkout.</p>
          <div class="billing-actions">
            <button class="gt-button accent" @click="goToSystemCheckout">Actualizar suscripcion base</button>
            <button class="gt-button soft" @click="activeTab = 'modulos'">Administrar modulos</button>
          </div>
          <pre class="debug-box">{{ billingSnapshot }}</pre>
        </article>
      </section>
    </div>
  </section>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'AccountView',
  data() {
    return {
      loading: false,
      saving: false,
      activeTab: 'perfil',
      summary: null,
      message: '',
      messageType: 'success',
      profileForm: { name: '', first_name: '', last_name: '' },
      companyForm: { commercial_name: '', legal_name: '', rfc: '', email: '', phone: '' },
      branchForm: { name: '', city: '' },
      tabs: [
        { key: 'perfil', label: 'Perfil' },
        { key: 'suscripcion', label: 'Suscripcion' },
        { key: 'sucursales', label: 'Sucursales' },
        { key: 'modulos', label: 'Modulos' },
        { key: 'usuarios', label: 'Usuarios' },
        { key: 'pagos', label: 'Pagos' },
      ],
    };
  },
  computed: {
    user() { return this.$store.state.user; },
    subscription() { return this.summary?.subscription || this.$store.state.context?.subscription || {}; },
    modules() { return this.summary?.modules || []; },
    branches() { return this.summary?.branches || []; },
    users() { return this.summary?.users || []; },
    limits() { return this.summary?.limits || {}; },
    usage() { return this.summary?.usage || {}; },
    canTestActivate() { return this.$store.getters.hasRole('system_admin') || this.$store.getters.hasRole('company_admin'); },
    daysRemainingText() {
      const days = this.subscription?.days_remaining;
      if (days === null || days === undefined) return 'Sin fecha';
      return `${days} dias`;
    },
    usageItems() {
      const modulesLimit = (this.limits.modules_additional ?? 0) + this.modules.filter((module) => module.is_core).length;
      return [
        { key: 'branches', label: 'Sucursales', used: this.usage.branches || 0, limit: this.limits.branches ?? 'N/A' },
        { key: 'users', label: 'Usuarios', used: this.usage.users || 0, limit: this.limits.users ?? 'N/A' },
        { key: 'modules', label: 'Modulos', used: this.modules.filter((module) => module.active).length, limit: modulesLimit || 'N/A' },
      ].map((item) => ({ ...item, percent: this.percent(item.used, item.limit) }));
    },
    billingSnapshot() {
      return JSON.stringify({ subscription: this.subscription, usage: this.usage, limits: this.limits }, null, 2);
    },
  },
  mounted() {
    this.loadSummary();
  },
  methods: {
    async loadSummary() {
      this.loading = true;
      try {
        const { data } = await api.get('/api/account/summary');
        this.summary = data;
        if (data.context) this.$store.commit('setContext', data.context);
        this.fillForms();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo cargar la cuenta.');
      } finally {
        this.loading = false;
      }
    },
    fillForms() {
      this.profileForm = {
        name: this.user?.name || '',
        first_name: this.user?.first_name || '',
        last_name: this.user?.last_name || '',
      };
      this.companyForm = {
        commercial_name: this.summary?.company?.name || '',
        legal_name: this.summary?.company?.legal_name || '',
        rfc: this.summary?.company?.rfc || '',
        email: this.summary?.company?.email || '',
        phone: this.summary?.company?.phone || '',
      };
    },
    async saveProfile() {
      this.saving = true;
      try {
        const { data } = await api.put('/api/account/profile', this.profileForm);
        this.$store.commit('setSession', { user: data.user });
        this.showSuccess(data.message);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo guardar el perfil.');
      } finally {
        this.saving = false;
      }
    },
    async saveCompany() {
      this.saving = true;
      try {
        const { data } = await api.put('/api/account/company', this.companyForm);
        this.showSuccess(data.message);
        await this.loadSummary();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo guardar la empresa.');
      } finally {
        this.saving = false;
      }
    },
    async createBranch() {
      this.saving = true;
      try {
        const { data } = await api.post('/api/account/branches', this.branchForm);
        this.branchForm = { name: '', city: '' };
        this.showSuccess(data.message);
        await this.loadSummary();
      } catch (error) {
        const payload = error.response?.data || {};
        if (payload.requires_upgrade && payload.checkout_url) {
          window.location.href = payload.checkout_url;
          return;
        }
        this.showError(payload.error || 'No se pudo crear la sucursal.');
      } finally {
        this.saving = false;
      }
    },
    async goToSystemCheckout() {
      try {
        const { data } = await api.post('/api/billing/checkout/system');
        if (data.checkout_url) window.location.href = data.checkout_url;
      } catch (error) {
        this.showError(error.response?.data?.error || 'La pasarela base aun no esta configurada.');
      }
    },
    async activateModule(code) {
      try {
        const { data } = await api.post(`/api/account/modules/${code}/activate`);
        if (data.checkout_url) window.location.href = data.checkout_url;
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo iniciar la activacion.');
      }
    },
    async activateForTesting(code) {
      try {
        const { data } = await api.post(`/api/billing/testing/modules/${code}/activate`);
        this.showSuccess(data.message || 'Modulo activado.');
        await this.loadSummary();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo activar para pruebas.');
      }
    },
    percent(used, limit) {
      if (!Number.isFinite(Number(limit)) || Number(limit) <= 0) return 0;
      return Math.min(100, Math.round((Number(used) / Number(limit)) * 100));
    },
    money(value, currency = 'MXN') {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value || 0));
    },
    formatDate(value) {
      if (!value) return 'Sin fecha configurada';
      return new Date(value).toLocaleString('es-MX');
    },
    showSuccess(text) { this.message = text; this.messageType = 'success'; },
    showError(text) { this.message = text; this.messageType = 'error'; },
  },
};
</script>

<style lang="scss" scoped>
.hero { padding: 2rem; display: flex; justify-content: space-between; gap: 1rem; align-items: center; }
h1 { margin: .65rem 0 .3rem; font-size: clamp(2rem, 4vw, 3rem); }
.notice { margin: 1rem 0; padding: 1rem 1.2rem; border-radius: 18px; background: #ecfff9; color: #167366; font-weight: 800; }
.notice.error { background: #fff0ec; color: var(--gt-danger); }
.tabs { margin: 1rem 0; padding: .5rem; display: flex; flex-wrap: wrap; gap: .5rem; }
.tabs button { border: 0; background: transparent; border-radius: 999px; padding: .75rem 1rem; font-weight: 900; cursor: pointer; color: var(--gt-muted); }
.tabs button.active { background: var(--gt-primary); color: #fff; }
.account-grid { margin-top: 1rem; }
.panel { padding: 1.35rem; }
.panel h2 { margin: 0 0 1rem; }
.gt-label { margin-top: .75rem; }
.email-line { margin: 1rem 0; }
.compact-grid { gap: .8rem; }
.stat { padding: 1.2rem; }
.stat span, .usage-box span { color: var(--gt-muted); font-weight: 800; }
.stat strong { display: block; margin: .55rem 0; font-size: 1.35rem; }
.section-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.section-header h2 { margin-bottom: .25rem; }
.inline-form { display: grid; grid-template-columns: 1.4fr 1fr auto; gap: .75rem; margin-bottom: 1rem; }
.item-list { display: grid; gap: .75rem; }
.item-row { display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid var(--gt-border); border-radius: 18px; background: rgba(255,255,255,.7); }
.item-row small { display: block; margin-top: .2rem; color: var(--gt-muted); }
.usage-box { padding: 1rem; border: 1px solid var(--gt-border); border-radius: 18px; background: #fff; }
.usage-box strong { display: block; margin: .45rem 0; font-size: 1.25rem; }
.progress { height: .45rem; border-radius: 999px; background: var(--gt-primary-soft); overflow: hidden; }
.progress i { display: block; height: 100%; background: var(--gt-primary); border-radius: inherit; }
.module-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; }
.module-card { border: 1px solid var(--gt-border); border-radius: 20px; padding: 1rem; background: #fff; }
.module-card h3 { margin: .85rem 0 .35rem; }
.module-card p { color: var(--gt-muted); min-height: 3.2rem; }
.module-card small { color: var(--gt-muted); font-weight: 800; }
.module-actions { display: flex; gap: .5rem; flex-wrap: wrap; margin-top: 1rem; }
.gt-pill.active { background: #ecfff9; color: #167366; }
.billing-actions { display: flex; gap: .75rem; flex-wrap: wrap; margin: 1rem 0; }
.debug-box { white-space: pre-wrap; background: #f8fafc; border: 1px solid var(--gt-border); border-radius: 18px; padding: 1rem; color: var(--gt-muted); max-height: 17rem; overflow: auto; }
@media (max-width: 920px) { .inline-form, .module-grid { grid-template-columns: 1fr; } .hero, .section-header, .item-row { flex-direction: column; align-items: flex-start; } }
</style>
